import React, { useState, useEffect } from "react";
import axios from "axios";
import { errMessage } from "../lib/errors";
import { catering, restaurantInfo } from "../data/mock";
import { Utensils, Truck, Sparkles, ArrowRight } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  utensils: Utensils,
  truck: Truck,
  sparkles: Sparkles
};

export default function Catering() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [eventType, setEventType] = useState("Private Catering");
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(20);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { user, authHeaders } = useAuth();

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast({ title: "Almost there", description: "Please add your name and email so we can get back to you." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/catering`,
        {
          name, email,
          phone: user?.phone || undefined,
          event_type: eventType,
          event_date: date || undefined,
          guests,
          message: message || undefined
        },
        { headers: authHeaders }
      );
      toast({
        title: "Enquiry received",
        description: `Thanks ${name} — mama will be in touch about your ${eventType.toLowerCase()}.`
      });
      if (!user) { setName(""); setEmail(""); }
      setMessage(""); setDate(""); setGuests(20);
    } catch (err) {
      toast({
        title: "Enquiry failed",
        description: errMessage(err, "Please try again or email us.")
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="catering" className="py-24 md:py-36 border-t border-[var(--mm-line)]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center mb-16 fade-in">
          <div className="section-label mb-4">Catering & Events</div>
          <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95] mb-6">
            {catering.headline}
          </h2>
          <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed text-lg">
            {catering.intro}
          </p>
        </div>

        {/* Services cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 fade-in">
          {catering.services.map((s) => {
            const Icon = iconMap[s.icon] || Utensils;
            return (
              <div
                key={s.title}
                className="p-8 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] hover:border-[var(--mm-accent)] transition-colors duration-500"
              >
                <div className="w-12 h-12 rounded-full border border-[var(--mm-accent)] flex items-center justify-center mb-6">
                  <Icon size={20} className="text-[var(--mm-accent)]" />
                </div>
                <h3 className="font-display text-2xl italic text-[var(--mm-cream)] mb-3 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-[var(--mm-cream-muted)] font-light leading-relaxed">
                  {s.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Enquiry form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start bg-[var(--mm-bg-alt)] p-8 md:p-12 border border-[var(--mm-line)] fade-in">
          <div>
            <div className="section-label mb-4">Enquire</div>
            <h3 className="font-display text-4xl md:text-5xl italic font-light text-[var(--mm-cream)] leading-tight mb-6">
              Tell us about your <span className="text-[var(--mm-accent)]">event.</span>
            </h3>
            <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed mb-8">
              Send us a message with the date, guest count and vibe you're after. We'll come back with a bespoke menu and quote — usually within 24 hours.
            </p>
            <div className="space-y-3 text-sm">
              <a href={`mailto:${restaurantInfo.contact.email}`} className="flex items-center gap-3 text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors">
                <ArrowRight size={14} /> {restaurantInfo.contact.email}
              </a>
              <a href={`tel:${restaurantInfo.contact.phone}`} className="flex items-center gap-3 text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors">
                <ArrowRight size={14} /> {restaurantInfo.contact.phoneDisplay}
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
              <label className="block">
                <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Event Type</span>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                >
                  {catering.services.map((s) => (
                    <option key={s.title} className="bg-[var(--mm-bg)]">{s.title}</option>
                  ))}
                  <option className="bg-[var(--mm-bg)]">Corporate / Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Guests</span>
                <input
                  type="number"
                  min="1"
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Event Date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
            </label>

            <label className="block">
              <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Tell us about it</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Venue, vibe, dietary needs, favourite dishes..."
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none resize-none"
              />
            </label>

            <button type="submit" disabled={submitting} className="btn-primary w-full text-center disabled:opacity-60">
              {submitting ? "Sending..." : catering.cta}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
